
import { useEffect, useRef } from 'react';
import { getNetworkStatus } from '@/utils/networkMonitor';
import { logPerformanceMonitor } from '@/utils/consoleLogger';

export const PerformanceMonitor = () => {
  const metricsLogged = useRef(false);

  useEffect(() => {
    logPerformanceMonitor('PerformanceMonitor initialized');
    
    // Skip in development to avoid console noise
    if (process.env.NODE_ENV === 'development') {
      logPerformanceMonitor('Development mode detected, full logging enabled');
    }
    
    // Initialize performance observer
    if ('PerformanceObserver' in window) {
      try {
        logPerformanceMonitor('Setting up performance observers');
        
        // Create observers for key metrics
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          const lcp = lastEntry.startTime;
          const networkStatus = getNetworkStatus();
          
          logPerformanceMonitor(`LCP: ${Math.round(lcp)}ms on ${networkStatus.effectiveType} connection`);
          
          // Log slow LCP specifically for analysis
          if (lcp > 2500) {
            logPerformanceMonitor(`Slow LCP detected: ${Math.round(lcp)}ms`);
          }
        });
        
        const fidObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          logPerformanceMonitor(`FID: ${Math.round(lastEntry.startTime)}ms`);
        });
        
        const clsObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          let clsScore = 0;
          
          entries.forEach(entry => {
            // @ts-ignore - CLS is not well-typed
            clsScore += entry.value;
          });
          
          logPerformanceMonitor(`CLS: ${clsScore.toFixed(3)}`);
        });
        
        // Observe the metrics
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
        fidObserver.observe({ type: 'first-input', buffered: true });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
        logPerformanceMonitor('Performance observers registered');
        
        // Log navigation timing with fixed approach
        const logNavigationTiming = () => {
          if (metricsLogged.current) return;
          
          logPerformanceMonitor('Attempting to log navigation timing');
          
          // Ensure navigation timing data is available
          const entries = performance.getEntriesByType('navigation');
          if (entries.length === 0) {
            // Try again later if no entries yet
            logPerformanceMonitor('No navigation entries available yet, retrying soon');
            setTimeout(logNavigationTiming, 500);
            return;
          }
          
          const timing = entries[0] as PerformanceNavigationTiming;
          
          // Check that DOM complete has a valid value
          if (timing.domComplete > 0) {
            logPerformanceMonitor(`Navigation: TTFB=${Math.round(timing.responseStart)}ms, DOMInteractive=${Math.round(timing.domInteractive)}ms, DOMComplete=${Math.round(timing.domComplete)}ms, LoadEvent=${Math.round(timing.loadEventEnd)}ms`);
            metricsLogged.current = true;
          } else {
            // If dom complete is 0, try again a bit later
            logPerformanceMonitor('DOM complete timing not available yet, retrying soon');
            setTimeout(logNavigationTiming, 500);
          }
        };
        
        // Start logging navigation timing after a short delay to ensure load event fires
        window.addEventListener('load', () => {
          logPerformanceMonitor('Window load event fired, logging navigation timing soon');
          setTimeout(logNavigationTiming, 100);
        });
        
        // Fallback in case load event doesn't fire or timing is wrong
        setTimeout(() => {
          logPerformanceMonitor('Fallback timing logger triggered');
          logNavigationTiming();
        }, 3000);
        
        return () => {
          logPerformanceMonitor('Cleaning up performance observers');
          lcpObserver.disconnect();
          fidObserver.disconnect();
          clsObserver.disconnect();
        };
      } catch (e) {
        console.error('[Performance] Error setting up performance monitoring:', e);
      }
    } else {
      logPerformanceMonitor('PerformanceObserver API not available in this browser');
    }
  }, []);
  
  return null;
};
